import React, { useCallback, useEffect, useRef } from 'react';
import { useSetAtom } from 'jotai';
import type {
  FilterWrapperParams,
  IAfterGuiAttachedParams,
} from 'ag-grid-community';
import type { CustomFilterDisplayProps } from 'ag-grid-react';
import { useGridFilterDisplay } from 'ag-grid-react';
import { SearchField } from '@wk/components-v3-react16';
import { dashboardFilterAtom } from '../../filterAtom';
import styles from './FileNameFilter.module.scss';

interface FileNameFilterModel {
  fileName: string;
}

interface FileNameFilterProperties
  extends CustomFilterDisplayProps<FileNameFilterModel>,
    FilterWrapperParams {}

const FileNameFilter = (properties: FileNameFilterProperties) => {
  const { model, onStateChange, state } = properties;
  const setDashboardFilter = useSetAtom(dashboardFilterAtom);
  const inputReference = useRef<HTMLInputElement>(null);

  const afterGuiAttached = useCallback(
    (parameters?: IAfterGuiAttachedParams) => {
      if (!parameters?.suppressFocus) {
        inputReference.current?.focus();
      }
    },
    []
  );

  useGridFilterDisplay({
    afterGuiAttached,
  });

  const updateAtom = useCallback(
    (newModel: FileNameFilterModel | null) =>
      setDashboardFilter((previousFilter) => ({
        ...previousFilter,
        fileName: newModel?.fileName || null,
      })),
    [setDashboardFilter]
  );

  const previousModelReference = useRef<FileNameFilterModel | null>(null);

  useEffect(() => {
    const currentModelFileName = model?.fileName || null;
    const previousModelFileName =
      previousModelReference.current?.fileName || null;

    if (currentModelFileName !== previousModelFileName) {
      updateAtom(model);
      previousModelReference.current = model;
    }
  }, [model, updateAtom]);

  const currentFileName = state.model?.fileName || '';

  const handleInput = (event: React.FormEvent) => {
    const { value } = event.target as HTMLInputElement;
    const newModel = value ? { fileName: value } : null;
    onStateChange({ model: newModel });
  };

  const handleClearInput = () => {
    onStateChange({ model: null });
  };

  const hasClearIcon = currentFileName.length > 0;

  return (
    <div className={styles.container}>
      <SearchField
        searchButtonId="file-name-search-button"
        size="large"
        hasClearIcon={hasClearIcon}
        onClear={handleClearInput}
      >
        <input
          ref={inputReference}
          type="search"
          name="file-name-search"
          aria-labelledby="file-name-search-button"
          autoComplete="off"
          onInput={handleInput}
          placeholder="Search by File Name..."
          value={currentFileName}
        />
      </SearchField>
    </div>
  );
};

export default FileNameFilter;
